import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, ArrowDown, Calendar, Clock, MapPin, InstagramLogo, SpinnerGap } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { useSeo } from "../hooks/useSeo";
import { EventCard } from "../components/EventCard";

const INTERESTS = [
  "Color & Blond",
  "Cutting & Styling",
  "Extensions",
  "Business & Salonmanagement",
  "Coaching",
  "Internationale Artists",
  "Community-Events",
];

const COMMUNITY_CATEGORIES = ["Community", "Kundinnen-Event"];

const EVENT_GROUPS = [
  { key: "education", title: "Masterclasses & Seminare", hint: "Für Friseur:innen", match: ev => !COMMUNITY_CATEGORIES.includes(ev.category) && !ev.is_free },
  { key: "community", title: "Community & Salon-Events", hint: "Offen für alle", match: ev => COMMUNITY_CATEGORIES.includes(ev.category) || ev.is_free },
];

const AUDIENCES = [
  {
    label: "Für Stylist:innen & Mitarbeiter:innen",
    title: "Wachse über den Salonalltag hinaus.",
    points: [
      "Masterclasses & Look-and-Learn-Days mit internationalen Artists",
      "Technik-Seminare zu Blond, Balayage, AirTouch & Extensions",
      "Kostenlose Community-Abende zum Netzwerken",
      "Als Erste:r erfahren, wenn neue Termine online gehen",
    ],
    cta: "Community beitreten",
    testId: "audience-stylist-cta",
  },
  {
    label: "Für Saloninhaber:innen",
    title: "Team-Education & neue Perspektiven.",
    points: [
      "Weiterbildung für dein gesamtes Team an einem Ort",
      "Business-, Coaching- & Salonmanagement-Impulse",
      "Austausch mit anderen Inhaber:innen aus der Region",
      "Co-Working & Stuhlmiete im JS Blond Salon",
    ],
    cta: "Co-Working entdecken",
    to: "/coworking",
    testId: "audience-owner-cta",
  },
];

const PILLARS = [
  { title: "TECHNIQUE", desc: "Neue Techniken, kreative Impulse und professionelle Weiterbildung für den Salonalltag." },
  { title: "MASTERCLASSES", desc: "Intensive Education mit erfahrenen Educators und internationalen Artists." },
  { title: "BUSINESS", desc: "Neue Perspektiven für Salonmanagement, wirtschaftlichen Erfolg und unternehmerische Weiterentwicklung." },
  { title: "COACHING", desc: "Persönliche Weiterentwicklung, neue Denkansätze und praxisorientierte Trainings." },
  { title: "COMMUNITY", desc: "Kostenlose Veranstaltungen, Networking und Begegnungen mit anderen Menschen aus der Friseurbranche." },
];

const EducationPage = () => {
  const [formData, setFormData] = useState({
    salon: "", first_name: "", zip_code: "", city: "", email: "",
    interests: [], wishlist: "", newsletter_consent: false, source: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useSeo({
    title: "JS Blond Education Hub | Friseur Seminare, Masterclasses & Community Heilbronn",
    description: "Education & Community für Friseur:innen in Heilbronn/Leingarten: Masterclasses mit internationalen Artists, Seminare, kostenlose Community-Events & Newsletter. Sei Teil der JS Blond Community.",
    path: "/education",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const src = params.get("src") || params.get("utm_source") || "website";
    setFormData(prev => ({ ...prev, source: src }));

    fetch(`${API_URL}/api/events`)
      .then(r => r.json())
      .then(data => setEvents(data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.salon || !formData.first_name || !formData.zip_code || !formData.city || !formData.email) {
      toast.error("Bitte fülle alle Pflichtfelder aus.");
      return;
    }
    setIsSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/api/education/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      const data = await resp.json();
      if (data.already_member) {
        toast.info("Du bist bereits Teil unserer Community!");
      }
      setIsSubmitted(true);
    } catch {
      toast.error("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#111] to-[#0a0a0a]" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.3em] mb-6"
          >
            The next big thing in hair.
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="text-4xl sm:text-6xl lg:text-8xl font-bold text-white leading-[0.9] tracking-tight mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            ISN'T A TREND.<br />
            <span className="text-[#D4AF37]">IT'S A PLACE.</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.4em] mb-3">
              JS Blond Education Hub
            </p>
            <p className="text-white/90 text-lg sm:text-xl font-light mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ein Ort. Unzählige Möglichkeiten.
            </p>
            <p className="text-white/50 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed mb-10">
              Mit dem JS Blond Education Hub schaffen wir einen Treffpunkt für die Friseurbranche. 
              Einen Ort, an dem Friseure zusammenkommen, voneinander lernen und gemeinsam das nächste Level erreichen.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollTo("community")}
                data-testid="hero-join-btn"
                className="px-8 py-4 bg-[#D4AF37] text-[#0a0a0a] text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-colors duration-300"
              >
                Join the Community
              </button>
              <button
                onClick={() => scrollTo("events")}
                data-testid="hero-events-btn"
                className="px-8 py-4 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/10 transition-colors duration-300"
              >
                Upcoming Events
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="mt-16"
          >
            <ArrowDown size={24} className="text-white/30 mx-auto animate-bounce" />
          </motion.div>
        </div>
      </section>

      {/* ===== COMMUNITY SIGNUP ===== */}
      <section id="community" className="py-20 sm:py-32 bg-[#F5F3EE]">
        <div className="max-w-3xl mx-auto px-6">
          {!isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2
                className="text-3xl sm:text-5xl font-bold text-[#0a0a0a] mb-4 tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                BE PART OF<br />
                <span className="text-[#D4AF37]">WHAT'S NEXT.</span>
              </h2>
              <p className="text-[#555] text-sm sm:text-base leading-relaxed mb-10 max-w-xl">
                Für 2027 ist Großes geplant. Sei von Anfang an dabei, erfahre als Erstes von unseren 
                kommenden Highlights und verrate uns, welche Artists und Themen du dir wünschst.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input name="salon" value={formData.salon} onChange={handleChange} required placeholder="Salon *" data-testid="edu-salon-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#ddd] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" />
                  <input name="first_name" value={formData.first_name} onChange={handleChange} required placeholder="Vorname *" data-testid="edu-name-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#ddd] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <input name="zip_code" value={formData.zip_code} onChange={handleChange} required placeholder="PLZ *" data-testid="edu-zip-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#ddd] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" />
                  <input name="city" value={formData.city} onChange={handleChange} required placeholder="Ort *" data-testid="edu-city-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#ddd] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors sm:col-span-2" />
                </div>
                <input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="E-Mail-Adresse *" data-testid="edu-email-input"
                  className="w-full px-4 py-3.5 bg-white border border-[#ddd] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" />

                {/* Interests */}
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-3">Welche Themen interessieren dich?</p>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map(interest => (
                      <button
                        key={interest}
                        type="button"
                        onClick={() => toggleInterest(interest)}
                        className={`px-4 py-2 text-xs tracking-wide border transition-all duration-200 ${
                          formData.interests.includes(interest)
                            ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                            : "bg-white text-[#555] border-[#ddd] hover:border-[#999]"
                        }`}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wishlist */}
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-2">Your Wishlist.</p>
                  <p className="text-sm text-[#777] mb-3">Welchen Artist oder welches Education-Thema wünschst du dir für 2027?</p>
                  <textarea name="wishlist" value={formData.wishlist} onChange={handleChange} rows={3} data-testid="edu-wishlist-input"
                    className="w-full px-4 py-3 bg-white border border-[#ddd] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                    placeholder="z.B. Vivienne Mackinder, Business-Coaching, Balayage Advanced..." />
                </div>

                {/* Newsletter Consent */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="newsletter_consent" checked={formData.newsletter_consent} onChange={handleChange}
                    className="mt-1 accent-[#D4AF37]" />
                  <span className="text-xs text-[#777] leading-relaxed">
                    Ich möchte Informationen zum Education-Programm, Veranstaltungen und dem Education-Kalender per E-Mail erhalten. 
                    Ich kann mich jederzeit abmelden. Es gelten die <Link to="/datenschutz" className="underline hover:text-[#0a0a0a]">Datenschutzbestimmungen</Link>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="edu-submit-btn"
                  className="w-full py-4 bg-[#0a0a0a] text-white text-xs uppercase tracking-[0.3em] font-bold hover:bg-[#D4AF37] hover:text-[#0a0a0a] transition-colors duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? "Wird gesendet..." : "I'M IN!"}
                </button>
              </form>
            </motion.div>
          ) : (
            /* Confirmation */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <h2
                className="text-3xl sm:text-5xl font-bold text-[#0a0a0a] mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                YOU'RE ALMOST IN.
              </h2>
              <p className="text-[#555] text-base mb-8 max-w-md mx-auto">
                Vielen Dank für dein Interesse an unserem Education Hub! Bestätige jetzt deine 
                E-Mail-Adresse und werde Teil unserer Community.
              </p>
              <CheckCircle size={48} className="text-[#D4AF37] mx-auto mb-8" weight="fill" />

              {/* Instagram CTA */}
              <div className="bg-[#0a0a0a] p-8 mt-8">
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-3">Stay Connected.</p>
                <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
                  Folge uns auf Instagram und begleite uns beim Aufbau unserer Education-Community. 
                  Freu dich auf erste Einblicke, neue Artists und kommende Veranstaltungen.
                </p>
                <a
                  href="https://www.instagram.com/jennifervallentin"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="edu-instagram-btn"
                  className="inline-flex items-center gap-2 px-8 py-3 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/10 transition-colors"
                >
                  <InstagramLogo size={18} weight="fill" />
                  Follow us on Instagram
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* ===== UPCOMING EVENTS ===== */}
      <section id="events" className="py-20 sm:py-32 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Upcoming</p>
            <h2
              className="text-3xl sm:text-5xl font-bold text-white tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              EVENTS
            </h2>
          </motion.div>

          <div className="space-y-8" data-testid="events-list">
            {eventsLoading ? (
              <div className="flex justify-center py-12">
                <SpinnerGap size={32} className="animate-spin text-[#D4AF37]" />
              </div>
            ) : events.length === 0 ? (
              <div className="bg-[#1a1a1a] border border-white/10 p-10 text-center" data-testid="events-empty">
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-3">Stay tuned</p>
                <p className="text-white/60 text-sm max-w-md mx-auto">
                  Neue Events werden in Kürze veröffentlicht. Werde Teil der Community und erfahre es als Erstes.
                </p>
              </div>
            ) : (
              EVENT_GROUPS.map(group => {
                const items = events.filter(ev => group.match(ev));
                if (items.length === 0) return null;
                return (
                  <div key={group.key} className="space-y-6" data-testid={`events-group-${group.key}`}>
                    <div className="flex items-baseline gap-4 pt-4">
                      <h3 className="text-white text-sm font-bold uppercase tracking-[0.25em]">{group.title}</h3>
                      <span className="flex-grow h-px bg-white/10" />
                      <span className="text-white/40 text-xs">{group.hint}</span>
                    </div>
                    {items.map((ev, i) => <EventCard key={ev.id} ev={ev} index={i} apiUrl={API_URL} />)}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* ===== FOR STYLISTS & SALON OWNERS ===== */}
      <section className="py-20 sm:py-28 bg-[#111] border-t border-white/5" data-testid="audience-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Für wen</p>
            <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              FÜR STYLIST:INNEN &<br />
              <span className="text-[#D4AF37]">SALONINHABER:INNEN.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {AUDIENCES.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-[#111] p-8 sm:p-10 flex flex-col"
              >
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-3">{a.label}</p>
                <h3 className="text-white text-xl sm:text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{a.title}</h3>
                <ul className="space-y-2 mb-8 flex-grow">
                  {a.points.map(p => (
                    <li key={p} className="flex items-start gap-3 text-white/60 text-sm leading-relaxed">
                      <span className="mt-2 w-1 h-1 bg-[#D4AF37] flex-shrink-0" />{p}
                    </li>
                  ))}
                </ul>
                {a.to ? (
                  <Link to={a.to} data-testid={a.testId} className="self-start px-6 py-3 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-white hover:text-[#0a0a0a] transition-colors duration-300">
                    {a.cta}
                  </Link>
                ) : (
                  <button onClick={() => scrollTo("community")} data-testid={a.testId} className="self-start px-6 py-3 bg-[#D4AF37] text-[#0a0a0a] text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-colors duration-300">
                    {a.cta}
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== EDUCATION & COMMUNITY PILLARS ===== */}
      <section className="py-20 sm:py-32 bg-[#F5F3EE]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2
              className="text-3xl sm:text-5xl font-bold text-[#0a0a0a] tracking-tight mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              MORE THAN EDUCATION.
            </h2>
            <p className="text-[#D4AF37] text-sm uppercase tracking-[0.3em]">Learn. Connect. Grow.</p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {PILLARS.map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#0a0a0a] p-5 flex flex-col"
              >
                <h3 className="text-[#D4AF37] text-xs font-bold uppercase tracking-[0.15em] mb-3">{pillar.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CALENDAR 2027 TEASER ===== */}
      <section className="py-20 sm:py-32 bg-[#0a0a0a] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-6">Coming soon</p>
            <h2
              className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              2027.<br />
              SOMETHING BIG<br />
              <span className="text-[#D4AF37]">IS COMING.</span>
            </h2>
            <p className="text-white/50 text-sm sm:text-base max-w-xl mx-auto leading-relaxed mb-10">
              Unser Education-Kalender 2027 erscheint im Dezember. Werde Teil unserer Community 
              und erfahre als Erstes, welche Veranstaltungen wir für dich geplant haben.
            </p>
            <button
              onClick={() => scrollTo("community")}
              data-testid="calendar-early-access-btn"
              className="px-10 py-4 bg-[#D4AF37] text-[#0a0a0a] text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-colors duration-300"
            >
              Get Early Access
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===== INSTAGRAM CTA ===== */}
      <section className="py-16 bg-[#F5F3EE]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-3">Stay Connected.</p>
          <p className="text-[#555] text-sm mb-6 max-w-md mx-auto">
            Folge uns auf Instagram und begleite uns beim Aufbau unserer Education-Community.
          </p>
          <a
            href="https://www.instagram.com/jennifervallentin"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="edu-footer-instagram"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#0a0a0a] text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#D4AF37] hover:text-[#0a0a0a] transition-colors duration-300"
          >
            <InstagramLogo size={18} weight="fill" />
            Follow @jennifervallentin
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EducationPage;
